import React from 'react';
import { EditIcon, CheckCircle2Icon, XCircleIcon, CalendarIcon, HashIcon, Trash2Icon, UserIcon } from 'lucide-react';
import { motion } from 'framer-motion';
export interface Invoice {
  id: string;
  supplier: string;
  invoiceNumber: string;
  date: string;
  category: 'Sikkerhed' | 'Værktøj' | 'Materialer' | 'Andet';
  type: 'Ladeboks' | 'Andet';
  amount: number;
  paid: boolean;
  customerId?: string;
  customerName?: string;
  notes?: string;
}
interface InvoiceCardProps {
  invoice: Invoice;
  index: number;
  onEdit: (invoice: Invoice) => void;
  onDelete: (invoice: Invoice) => void;
  readOnly?: boolean;
}
const getCategoryColor = (category: string) => {
  switch (category) {
    case 'Sikkerhed':
      return 'bg-red-100 text-red-700 border-red-200';
    case 'Værktøj':
      return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'Materialer':
      return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'Andet':
      return 'bg-stone-100 text-stone-700 border-stone-200';
    default:
      return 'bg-stone-100 text-stone-700 border-stone-200';
  }
};
const getTypeColor = (type: string) => {
  switch (type) {
    case 'Ladeboks':
      return 'bg-purple-100 text-purple-700 border-purple-200';
    case 'Andet':
      return 'bg-stone-100 text-stone-700 border-stone-200';
    default:
      return 'bg-stone-100 text-stone-700 border-stone-200';
  }
};
export function InvoiceCard({
  invoice,
  index,
  onEdit,
  onDelete,
  readOnly = false
}: InvoiceCardProps) {
  return <motion.article initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3,
    delay: index * 0.05
  }} className="bg-white rounded-xl p-5 shadow-sm border border-stone-100 hover:shadow-md transition-shadow duration-200" data-id="element-363" data-mp="26e1bpk88z166">
      <div className="flex items-start justify-between mb-3" data-id="element-364" data-mp="pwdklgrfj17d">
        <div className="flex-1" data-id="element-365" data-mp="kyvm6kb1g4dm">
          <h3 className="text-lg font-semibold text-stone-900 mb-1" data-id="element-366" data-mp="l1zx00j0dyd9">
            {invoice.supplier}
          </h3>
          <div className="flex items-center gap-2 text-sm text-stone-500" data-id="element-367" data-mp="3d77be9gwzkyl">
            <HashIcon className="w-3.5 h-3.5" data-id="element-368" data-mp="3d534rjrms7um" />
            <span data-id="element-369" data-mp="2z87i2oigihw3">{invoice.invoiceNumber}</span>
          </div>
        </div>
        <div className="flex items-center gap-2" data-id="element-370" data-mp="34692bcrmcn95">
          {!readOnly && <>
              <button onClick={() => onEdit(invoice)} className="p-1.5 text-stone-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-all" aria-label="Rediger faktura" data-id="element-371" data-mp="2phml15ifllfa">
                <EditIcon className="w-4 h-4" data-id="element-372" data-mp="2gi7xoexk9qz0" />
              </button>
              <button onClick={() => onDelete(invoice)} className="p-1.5 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all" aria-label="Slet faktura" data-id="element-373" data-mp="2lfhyeqqehp25">
                <Trash2Icon className="w-4 h-4" data-id="element-374" data-mp="2bhbygeo5mszx" />
              </button>
            </>}
          {invoice.paid ? <div className="p-1.5 bg-emerald-50 rounded-lg" data-id="element-375" data-mp="1rxv47pdj2abi">
              <CheckCircle2Icon className="w-4 h-4 text-emerald-600" data-id="element-376" data-mp="1nvrpmrvkapm0" />
            </div> : <div className="p-1.5 bg-red-50 rounded-lg" data-id="element-377" data-mp="21vstmg74id5p">
              <XCircleIcon className="w-4 h-4 text-red-600" data-id="element-378" data-mp="1wull2ex5p9xz" />
            </div>}
        </div>
      </div>

      <div className="space-y-2 mb-4" data-id="element-379" data-mp="2au4fk4x1omrt">
        <div className="flex items-center gap-2 text-sm" data-id="element-380" data-mp="2at30zqgtli5c">
          <CalendarIcon className="w-4 h-4 text-teal-600" data-id="element-381" data-mp="26u3zkfuas3in" />
          <span className="text-stone-500" data-id="element-382" data-mp="26rhsehbbjq0y">Dato:</span>
          <span className="text-stone-900 font-medium" data-id="element-383" data-mp="2kje9zrfd2hj3">
            {new Date(invoice.date).toLocaleDateString('da-DK')}
          </span>
        </div>

        {invoice.customerName && <div className="flex items-center gap-2 text-sm" data-id="element-384" data-mp="2ujycexem9s2w">
            <UserIcon className="w-4 h-4 text-blue-600" data-id="element-385" data-mp="2pjskrh8ck5kt" />
            <span className="text-stone-500" data-id="element-386" data-mp="2plddr2f0jggk">Kunde:</span>
            <span className="text-stone-900 font-medium" data-id="element-387" data-mp="mxolkq2pww99">
              {invoice.customerName}
            </span>
          </div>}

        <div className="flex items-center gap-2 flex-wrap" data-id="element-388" data-mp="rundd1ywsr4a">
          <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getTypeColor(invoice.type)}`} data-id="element-389" data-mp="wmevco401oit">
            {invoice.type}
          </span>
          <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getCategoryColor(invoice.category)}`} data-id="element-390" data-mp="3yanbvxygzcy">
            {invoice.category}
          </span>
          <span className={`px-3 py-1 text-xs font-medium rounded-full border ${invoice.paid ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-red-100 text-red-700 border-red-200'}`} data-id="element-391" data-mp="8z6y8tgq5lrv">
            {invoice.paid ? 'Betalt' : 'Ubetalt'}
          </span>
        </div>
      </div>

      {invoice.notes && <div className="text-sm text-stone-600 mb-4 line-clamp-2" dangerouslySetInnerHTML={{
      __html: invoice.notes
    }} data-id="element-392" data-mp="1u9xbijzzpswu" />}

      <div className="pt-4 border-t border-stone-100" data-id="element-393" data-mp="39twn68inxkjh">
        <div className="flex items-center justify-between" data-id="element-394" data-mp="39ug2rikubwuc">
          <span className="text-sm text-stone-500" data-id="element-395" data-mp="35264nf9knqgz">Beløb</span>
          <span className="text-xl font-bold text-stone-900" data-id="element-396" data-mp="350lee1uaitox">
            {invoice.amount.toLocaleString('da-DK')} kr
          </span>
        </div>
      </div>
    </motion.article>;
}